"use client";

// THE FOOTER, defined (John, 2026-10-21: "a more defined footer with more
// options"). Five short columns of places, every link a page that renders for
// a signed-out stranger, then one quiet row: the line, the badge, the socials.
// Pages still behind a release gate (developers, labs) are listed only once
// their gate opens, or for a founder on the proving ground, the same rule as
// the menu (lib/nav/siteNav). Shared by the homepage and the developer page.

import { gateOpen } from "../../../lib/release/gates";
import OrganismMark from "../../brand/OrganismMark";
import SocialLinks from "../../SocialLinks";
import ProductHuntBadge from "../../ProductHuntBadge";
import "./footer.css";

type Link = { label: string; href: string; gate?: "developersPublic" | "labsPublic" | "pricingRoom" };
const COLUMNS: Array<{ k: string; links: Link[] }> = [
  {
    k: "innernet",
    links: [
      { label: "your dashboard", href: "/dashboard" },
      { label: "connect an ai", href: "/connectors" },
      // only where the page carries the pricing room (the evolved homepage)
      { label: "pricing", href: "/#pricing", gate: "pricingRoom" },
      { label: "what's new", href: "/company?tab=updates" },
    ],
  },
  {
    k: "build",
    links: [
      { label: "developers", href: "/developers", gate: "developersPublic" },
      { label: "docs", href: "/docs" },
      { label: "agent memory", href: "/docs/agents" },
      { label: "the api", href: "/docs/api" },
      { label: "mcp tools", href: "/docs/mcp-tools" },
      { label: "npx innernet", href: "/docs/cli" },
    ],
  },
  {
    k: "the commons",
    links: [
      { label: "community", href: "/community" },
      { label: "skills", href: "/community/skills" },
      { label: "guides", href: "/community/guides" },
      { label: "innernet labs", href: "/labs", gate: "labsPublic" },
    ],
  },
  {
    k: "company",
    links: [
      { label: "about", href: "/company/about" },
      { label: "team", href: "/company/team" },
      { label: "blog", href: "/company" },
      { label: "compare", href: "/company/compare" },
      { label: "contact", href: "mailto:connect@innernet.live" },
    ],
  },
  {
    k: "trust",
    links: [
      { label: "security", href: "/security" },
      { label: "privacy", href: "/privacy" },
      { label: "terms", href: "/terms" },
      { label: "status", href: "/status" },
    ],
  },
];

export default function HomeFooter({ founder = false, pricing = true }: { founder?: boolean; pricing?: boolean }) {
  // a link to a room the page does not have is a link to nowhere
  const shown = (l: Link) => (l.gate === "pricingRoom" ? pricing : !l.gate || founder || gateOpen(l.gate));
  return (
    <footer className="hf" data-room="foot" aria-label="innernet">
      <div className="hf-in">
        <div className="hf-brand">
          <span className="hf-mark">
            <OrganismMark tone="gradient" size={22} />
            innernet
          </span>
          <p>the internet, but yours. one memory for every ai you use, kept by netti, opened only as far as you allow.</p>
        </div>
        <nav className="hf-cols" aria-label="footer">
          {COLUMNS.map((c) => (
            <div key={c.k} className="hf-col">
              <p className="hf-col__k">{c.k}</p>
              <ul>
                {c.links.filter(shown).map((l) => (
                  <li key={l.label}>
                    <a href={l.href}>{l.label}</a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </div>
      <div className="hf-bar">
        <span className="hf-bar__c">© 2026 innernet · made with care, for the people who use it</span>
        <span className="hf-bar__ph"><ProductHuntBadge theme="dark" width={190} /></span>
        <span className="hf-bar__social"><SocialLinks /></span>
      </div>
    </footer>
  );
}
